// ===========================================================================
// Denials Management — datos fake deterministas (Módulo G).
//
// No existe backend todavía. Cuando exista, este archivo se elimina y el
// service consumirá el schema `billing` (TAB_DENIALS / TAB_APPEALS /
// TAB_DENIAL_REASONS, alimentado por remits EDI 835) vía /api/billing/*.
// Ver docs/modulos/BillingV2/MODULO_G_DENIALS_MANAGEMENT.md
// ===========================================================================

import dayjs from "dayjs";
import { DENIAL_STATUS, ROOT_CAUSES } from "../common/constants";

const daysAgo = (n) => dayjs().subtract(n, "day").format("YYYY-MM-DD");
const daysAhead = (n) => dayjs().add(n, "day").format("YYYY-MM-DD");

/**
 * Worklist de denials. Cada fila: claim, payer, CARC + razón, monto, edad,
 * deadline de apelación, estado y detalle (causa raíz, CPT/DX, paciente).
 */
export const FAKE_DENIALS = [
  {
    id: "CLM-2026-04817",
    payer: "Humana MA",
    carc: "CO-197",
    carcReason: "Precertification/authorization absent",
    amount: 4200,
    denialDate: daysAgo(6),
    ageDays: 6,
    appealDeadline: daysAhead(54),
    status: DENIAL_STATUS.NEW,
    rootCause: ROOT_CAUSES.AUTH,
    patientName: "Rosa Linares",
    cipId: "CIP-211504",
    dos: daysAgo(21),
    cpt: "93306",
    dx: "I50.23",
    recommendedAction: "Appeal with retro-authorization request — payer allows retro auth within 90 days for urgent studies.",
    appealFiledDate: null,
    outcome: null,
  },
  {
    id: "CLM-2026-04722",
    payer: "UnitedHealthcare MA",
    carc: "CO-50",
    carcReason: "Not deemed medically necessary",
    amount: 3850,
    denialDate: daysAgo(12),
    ageDays: 12,
    appealDeadline: daysAhead(48),
    status: DENIAL_STATUS.IN_APPEAL,
    rootCause: ROOT_CAUSES.DOCUMENTATION,
    patientName: "Hector Maldonado",
    cipId: "CIP-203145",
    dos: daysAgo(34),
    cpt: "78452",
    dx: "I25.10",
    recommendedAction: "Appeal with chart notes documenting anginal symptoms and prior abnormal stress test.",
    appealFiledDate: daysAgo(4),
    outcome: null,
  },
  {
    id: "CLM-2026-04615",
    payer: "Aetna MA",
    carc: "CO-11",
    carcReason: "Diagnosis inconsistent with procedure",
    amount: 2640,
    denialDate: daysAgo(15),
    ageDays: 15,
    appealDeadline: daysAhead(45),
    status: DENIAL_STATUS.NEW,
    rootCause: ROOT_CAUSES.CODING,
    patientName: "Walter Briggs",
    cipId: "CIP-176342",
    dos: daysAgo(33),
    cpt: "95810",
    dx: "G47.30",
    recommendedAction: "Correct DX to G47.33 (documented OSA) and resubmit corrected claim.",
    appealFiledDate: null,
    outcome: null,
  },
  {
    id: "CLM-2026-04590",
    payer: "Humana MA",
    carc: "CO-29",
    carcReason: "Timely filing limit expired",
    amount: 1980,
    denialDate: daysAgo(18),
    ageDays: 18,
    appealDeadline: daysAhead(12),
    status: DENIAL_STATUS.IN_APPEAL,
    rootCause: ROOT_CAUSES.TIMELY_FILING,
    patientName: "Gloria Pineda",
    cipId: "CIP-214772",
    dos: daysAgo(140),
    cpt: "99214",
    dx: "M05.80",
    recommendedAction: "Appeal with proof of timely submission (clearinghouse acceptance report).",
    appealFiledDate: daysAgo(7),
    outcome: null,
  },
  {
    id: "CLM-2026-04545",
    payer: "Cigna MA",
    carc: "CO-97",
    carcReason: "Bundled into another service (NCCI)",
    amount: 760,
    denialDate: daysAgo(20),
    ageDays: 20,
    appealDeadline: daysAhead(40),
    status: DENIAL_STATUS.NEW,
    rootCause: ROOT_CAUSES.BUNDLING,
    patientName: "Carmen Soto",
    cipId: "CIP-225617",
    dos: daysAgo(36),
    cpt: "36415",
    dx: "I10",
    recommendedAction: "Review NCCI pair; if separately identifiable, resubmit with modifier 59 documentation.",
    appealFiledDate: null,
    outcome: null,
  },
  {
    id: "CLM-2026-04481",
    payer: "UnitedHealthcare MA",
    carc: "CO-22",
    carcReason: "Care may be covered by another payer (COB)",
    amount: 3120,
    denialDate: daysAgo(25),
    ageDays: 25,
    appealDeadline: daysAhead(35),
    status: DENIAL_STATUS.IN_APPEAL,
    rootCause: ROOT_CAUSES.ELIGIBILITY,
    patientName: "Oscar Villalba",
    cipId: "CIP-208119",
    dos: daysAgo(45),
    cpt: "99215",
    dx: "I25.2",
    recommendedAction: "Update COB with primary payer EOB and resubmit to secondary.",
    appealFiledDate: daysAgo(10),
    outcome: null,
  },
  {
    id: "CLM-2026-04412",
    payer: "Aetna MA",
    carc: "CO-18",
    carcReason: "Duplicate claim/service",
    amount: 540,
    denialDate: daysAgo(28),
    ageDays: 28,
    appealDeadline: daysAhead(32),
    status: DENIAL_STATUS.WRITTEN_OFF,
    rootCause: ROOT_CAUSES.DUPLICATE,
    patientName: "James Holt",
    cipId: "CIP-198223",
    dos: daysAgo(50),
    cpt: "80053",
    dx: "E11.9",
    recommendedAction: "True duplicate of paid claim CLM-2026-04410 — write-off confirmed.",
    appealFiledDate: null,
    outcome: "Written off — duplicate of paid claim",
  },
  {
    id: "CLM-2026-04388",
    payer: "Humana MA",
    carc: "CO-197",
    carcReason: "Precertification/authorization absent",
    amount: 5610,
    denialDate: daysAgo(31),
    ageDays: 31,
    appealDeadline: daysAhead(29),
    status: DENIAL_STATUS.IN_APPEAL,
    rootCause: ROOT_CAUSES.AUTH,
    patientName: "Beatriz Camacho",
    cipId: "CIP-217554",
    dos: daysAgo(52),
    cpt: "94660",
    dx: "J96.11",
    recommendedAction: "Appeal with documentation of urgent initiation of home O2/CPAP and retro-auth request.",
    appealFiledDate: daysAgo(15),
    outcome: null,
  },
  {
    id: "CLM-2026-04321",
    payer: "Cigna MA",
    carc: "CO-16",
    carcReason: "Claim lacks information (missing modifier)",
    amount: 890,
    denialDate: daysAgo(35),
    ageDays: 35,
    appealDeadline: daysAhead(25),
    status: DENIAL_STATUS.RECOVERED,
    rootCause: ROOT_CAUSES.CODING,
    patientName: "Dolores Ferrer",
    cipId: "CIP-219008",
    dos: daysAgo(60),
    cpt: "20610",
    dx: "M81.0",
    recommendedAction: "Corrected claim with modifier RT resubmitted.",
    appealFiledDate: daysAgo(28),
    outcome: "Recovered in full — paid on resubmission",
  },
  {
    id: "CLM-2026-04265",
    payer: "UnitedHealthcare MA",
    carc: "CO-50",
    carcReason: "Not deemed medically necessary",
    amount: 7240,
    denialDate: daysAgo(40),
    ageDays: 40,
    appealDeadline: daysAhead(20),
    status: DENIAL_STATUS.IN_APPEAL,
    rootCause: ROOT_CAUSES.DOCUMENTATION,
    patientName: "Samuel Ortega",
    cipId: "CIP-228430",
    dos: daysAgo(62),
    cpt: "74183",
    dx: "K70.30",
    recommendedAction: "Appeal with labs (thrombocytopenia, transaminitis) supporting MRI liver indication.",
    appealFiledDate: daysAgo(30),
    outcome: null,
  },
  {
    id: "CLM-2026-04190",
    payer: "Humana MA",
    carc: "CO-109",
    carcReason: "Claim not covered by this payer/contractor",
    amount: 1450,
    denialDate: daysAgo(44),
    ageDays: 44,
    appealDeadline: daysAhead(16),
    status: DENIAL_STATUS.NEW,
    rootCause: ROOT_CAUSES.ELIGIBILITY,
    patientName: "Felix Arroyo",
    cipId: "CIP-232901",
    dos: daysAgo(70),
    cpt: "84153",
    dx: "C61",
    recommendedAction: "Verify plan enrollment segment for DOS; redirect to correct payer if member switched plans.",
    appealFiledDate: null,
    outcome: null,
  },
  {
    id: "CLM-2026-04122",
    payer: "Aetna MA",
    carc: "CO-151",
    carcReason: "Payment adjusted — frequency exceeds allowed",
    amount: 980,
    denialDate: daysAgo(48),
    ageDays: 48,
    appealDeadline: daysAhead(12),
    status: DENIAL_STATUS.RECOVERED,
    rootCause: ROOT_CAUSES.CODING,
    patientName: "Maria Garcia",
    cipId: "CIP-204881",
    dos: daysAgo(75),
    cpt: "83036",
    dx: "E11.65",
    recommendedAction: "Appealed with medical necessity for quarterly A1c in uncontrolled DM.",
    appealFiledDate: daysAgo(40),
    outcome: "Recovered in full after first-level appeal",
  },
  {
    id: "CLM-2026-04067",
    payer: "Cigna MA",
    carc: "CO-4",
    carcReason: "Procedure inconsistent with modifier",
    amount: 1320,
    denialDate: daysAgo(52),
    ageDays: 52,
    appealDeadline: daysAhead(8),
    status: DENIAL_STATUS.NEW,
    rootCause: ROOT_CAUSES.CODING,
    patientName: "Lucia Trevino",
    cipId: "CIP-191236",
    dos: daysAgo(80),
    cpt: "99213",
    dx: "G20",
    recommendedAction: "Remove modifier 25 (no separately identifiable E/M) or document distinct service; deadline approaching.",
    appealFiledDate: null,
    outcome: null,
  },
  {
    id: "CLM-2026-03998",
    payer: "UnitedHealthcare MA",
    carc: "CO-29",
    carcReason: "Timely filing limit expired",
    amount: 2210,
    denialDate: daysAgo(58),
    ageDays: 58,
    appealDeadline: daysAhead(2),
    status: DENIAL_STATUS.NEW,
    rootCause: ROOT_CAUSES.TIMELY_FILING,
    patientName: "Albert Nguyen",
    cipId: "CIP-187990",
    dos: daysAgo(155),
    cpt: "99214",
    dx: "F32.9",
    recommendedAction: "URGENT: appeal deadline in 2 days — submit clearinghouse acceptance proof today.",
    appealFiledDate: null,
    outcome: null,
  },
  {
    id: "CLM-2026-03911",
    payer: "Humana MA",
    carc: "CO-96",
    carcReason: "Non-covered charge(s)",
    amount: 410,
    denialDate: daysAgo(63),
    ageDays: 63,
    appealDeadline: daysAgo(3),
    status: DENIAL_STATUS.WRITTEN_OFF,
    rootCause: ROOT_CAUSES.ELIGIBILITY,
    patientName: "Walter Briggs",
    cipId: "CIP-176342",
    dos: daysAgo(90),
    cpt: "97110",
    dx: "M62.81",
    recommendedAction: "Benefit excluded under plan; appeal deadline passed.",
    appealFiledDate: null,
    outcome: "Written off — non-covered benefit, deadline expired",
  },
  {
    id: "CLM-2026-03846",
    payer: "Aetna MA",
    carc: "CO-50",
    carcReason: "Not deemed medically necessary",
    amount: 4980,
    denialDate: daysAgo(70),
    ageDays: 70,
    appealDeadline: daysAhead(20),
    status: DENIAL_STATUS.RECOVERED,
    rootCause: ROOT_CAUSES.DOCUMENTATION,
    patientName: "Rosa Linares",
    cipId: "CIP-211504",
    dos: daysAgo(95),
    cpt: "93350",
    dx: "I50.23",
    recommendedAction: "Appealed with HF documentation and BNP trend.",
    appealFiledDate: daysAgo(55),
    outcome: "Recovered 80% after peer-to-peer review",
  },
];

/** Recuperaciones del mes (para recovery rate de la signal bar). */
export const FAKE_RECOVERY_SUMMARY = {
  deniedMtd: 28400,
  recoveredMtd: 11650,
  avgDaysToAppeal: 9.4,
};
